import { Select } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import ImageView from "~components/ImageView/ImageView.tsx";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import type { RoleStatuses } from "~enums/roleStatuses.ts";
import type { Roles } from "~enums/roles.ts";
import type { UpdateRole } from "~hooks/apiHooks/useUpdateRole.ts";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey.ts";
import { SeasonOfficialsTableColumns } from "../enums/seasonOfficialsTableColumns";
import type { SeasonOfficialsTableRow } from "../types/seasonOfficialsTableRow";

type Props = {
  isMassEditMode: boolean;
  officialRoles: Roles[];
  roleStatuses: RoleStatuses[];
  updateRole: (payload: UpdateRole) => Promise<void>;
};

export const useGetSeasonOfficialsTableColumns = ({
  isMassEditMode,
  officialRoles,
  roleStatuses,
  updateRole,
}: Props): Array<DataTableColumn<SeasonOfficialsTableRow>> => {
  const { formatMessage } = useIntl();
  const { sport = "" } = useParams();
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);

  const updateRoleField = async (roleId: string, payload: Omit<UpdateRole, "roleId">) => {
    setUpdatingRoleId(roleId);

    try {
      await updateRole({ roleId, ...payload });
    } finally {
      setUpdatingRoleId(null);
    }
  };

  return [
    {
      key: SeasonOfficialsTableColumns.Logo,
      title: formatMessage({ id: "seasonOfficials.table.column.logo" }),
      dataIndex: "avatarUrl",
      width: 50,
      render: (avatarUrl: string) => (
        <ImageView imageType={ImageTypes.PersonHead} imageUrl={avatarUrl} size={ImageSizes.Small} width={25} height={25} />
      ),
    },
    {
      key: SeasonOfficialsTableColumns.NameFullLocal,
      title: formatMessage({ id: "seasonOfficials.table.column.nameFullLocal" }),
      dataIndex: "nameFullLocal",
      width: 240,
      ellipsis: { showTitle: true },
    },
    {
      key: SeasonOfficialsTableColumns.PersonId,
      title: formatMessage({ id: "seasonOfficials.table.column.personId" }),
      dataIndex: "personId",
      width: 260,
    },
    {
      key: SeasonOfficialsTableColumns.ExternalId,
      title: formatMessage({ id: "seasonOfficials.table.column.externalId" }),
      dataIndex: "externalId",
      width: 180,
    },
    {
      key: SeasonOfficialsTableColumns.Role,
      title: formatMessage({ id: "seasonOfficials.table.column.role" }),
      dataIndex: "role",
      width: 240,
      sorter: true,
      render: (role: string, row) =>
        isMassEditMode ? (
          <Select
            disabled={updatingRoleId === row.roleId}
            onChange={(value) => void updateRoleField(row.roleId, { role: value as Roles })}
            options={officialRoles.map((officialRole) => ({
              label: formatMessage({ id: `roles.${getRoleTranslationKey(officialRole, sport)}` }),
              value: officialRole,
            }))}
            style={{ width: "100%" }}
            value={role}
          />
        ) : (
          formatMessage({ id: `roles.${getRoleTranslationKey(role as Roles, sport)}` })
        ),
    },
    {
      key: SeasonOfficialsTableColumns.Gender,
      title: formatMessage({ id: "seasonOfficials.table.column.gender" }),
      dataIndex: "gender",
      width: 120,
    },
    {
      key: SeasonOfficialsTableColumns.Nationality,
      title: formatMessage({ id: "seasonOfficials.table.column.nationality" }),
      dataIndex: "nationality",
      width: 120,
    },
    {
      key: SeasonOfficialsTableColumns.Status,
      title: formatMessage({ id: "seasonOfficials.table.column.status" }),
      dataIndex: "status",
      width: 140,
      render: (status: string, row) =>
        isMassEditMode ? (
          <Select
            disabled={updatingRoleId === row.roleId}
            onChange={(value) => void updateRoleField(row.roleId, { status: value as RoleStatuses })}
            options={roleStatuses.map((roleStatus) => ({
              label: formatMessage({ id: `roleStatus.${roleStatus}` }),
              value: roleStatus,
            }))}
            style={{ width: "100%" }}
            value={status}
          />
        ) : status ? (
          formatMessage({ id: `roleStatus.${status}` })
        ) : (
          ""
        ),
    },
    {
      key: SeasonOfficialsTableColumns.Bib,
      title: formatMessage({ id: "seasonOfficials.table.column.bib" }),
      dataIndex: "bib",
      width: 100,
    },
    {
      key: SeasonOfficialsTableColumns.DateFrom,
      title: formatMessage({ id: "seasonOfficials.table.column.dateFrom" }),
      dataIndex: "dateFrom",
      width: 140,
    },
    {
      key: SeasonOfficialsTableColumns.DateTo,
      title: formatMessage({ id: "seasonOfficials.table.column.dateTo" }),
      dataIndex: "dateTo",
      width: 140,
    },
    {
      key: SeasonOfficialsTableColumns.EntityGroupNameFullLocal,
      title: formatMessage({ id: "seasonOfficials.table.column.entityGroupNameFullLocal" }),
      dataIndex: "entityGroupNameFullLocal",
      width: 220,
      ellipsis: { showTitle: true },
    },
    {
      key: SeasonOfficialsTableColumns.EntityNameFullLocal,
      title: formatMessage({ id: "seasonOfficials.table.column.entityNameFullLocal" }),
      dataIndex: "entityNameFullLocal",
      width: 220,
      ellipsis: { showTitle: true },
    },
    {
      key: SeasonOfficialsTableColumns.RoleNumber,
      title: formatMessage({ id: "seasonOfficials.table.column.roleNumber" }),
      dataIndex: "roleNumber",
      width: 120,
    },
    {
      key: SeasonOfficialsTableColumns.RoleSubType,
      title: formatMessage({ id: "seasonOfficials.table.column.roleSubType" }),
      dataIndex: "roleSubType",
      width: 160,
    },
    {
      key: SeasonOfficialsTableColumns.RoleId,
      title: formatMessage({ id: "seasonOfficials.table.column.roleId" }),
      dataIndex: "roleId",
      width: 260,
    },
  ];
};
